import { useOverlayPortalRoot } from "@/components/modal/overlay.hooks";
import { COMMUNITY_SLACK_URL } from "@/utils/community-support";
import { usePylonEnabled } from "@/utils/hooks/use-pylon-enabled";
import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import { Icon, type IconName } from "@/components/icons/Icon";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import { SliderTabsV2, type SliderTabV2Item } from "@/components/interactive/TabsV2";
import { ViewportAwareFixedMenuV2 } from "@/components/interactive/ViewportAwareFixedMenuV2";
import { UserAvatar } from "@/components/pills/UserAvatar";
import { TooltipV2 } from "@/components/TooltipV2";
import { classNames } from "@/utils/classnames";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { usePagerDutyEscalation } from "@/utils/hooks/use-pagerduty-escalation";
import { usePylonChatBubbleControls } from "@/utils/pylon-init";
import { Routes } from "@/utils/routes";
import { signOutAndResetAnalytics } from "@/utils/sign-out";
import type { TeamUser } from "@/utils/teamService";
import { useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PROFILE_MENU_WIDTH_PX = 208;

interface ProfileMenuItem {
  id: string;
  label: string;
  icon: IconName;
  trailingIcon?: IconName;
  onSelect: () => void;
}

interface ProfileMenuSection {
  id: string;
  items: readonly ProfileMenuItem[];
}

/** The signed-in user, shaped for `UserAvatar`. */
export const useSignedInTeamUser = (): TeamUser | undefined => {
  const session = useSession().data;
  return useMemo(
    () =>
      session == null
        ? undefined
        : {
            id: session.userId,
            name: session.user?.name ?? undefined,
            email: session.user?.email ?? undefined,
            image: session.user?.image ?? session.image ?? undefined,
          },
    [session],
  );
};

const useProfileMenuSections = (): readonly ProfileMenuSection[] => {
  const router = useRouter();
  const { environmentId, projectId } = useRouteAndTeamInfo();
  const userEmail = useSession().data?.user?.email;
  const isEnterpriseSupport = useFlagsV2().enabled("enterprise_support_enabled");
  const { CHALK_DEPLOYMENT_MODEL } = useNextEnvVars();
  const isSelfHosted = CHALK_DEPLOYMENT_MODEL === "CUSTOMER_CLOUD";
  const { escalateIssue, isCreatingIncident } = usePagerDutyEscalation();
  const pylonEnabled = usePylonEnabled();
  const { markChatBubbleVisible } = usePylonChatBubbleControls();

  return useMemo(
    () => [
      {
        id: "support",
        items: [
          {
            id: "docs",
            label: "Docs",
            icon: "docs",
            trailingIcon: "arrowExternal" as const,
            onSelect: () => window.open("https://docs.chalk.ai", "_blank", "noopener,noreferrer"),
          },
          {
            id: "community-slack",
            label: "Community Slack",
            icon: "messageSquare",
            trailingIcon: "arrowExternal" as const,
            onSelect: () => window.open(COMMUNITY_SLACK_URL, "_blank", "noopener,noreferrer"),
          },
          ...(pylonEnabled
            ? [
                {
                  id: "customer-portal",
                  label: "Customer portal",
                  icon: "handshake" as const,
                  trailingIcon: "arrowExternal" as const,
                  onSelect: () =>
                    window.open(
                      "https://portal.usepylon.com/chalk",
                      "_blank",
                      "noopener,noreferrer",
                    ),
                },
                {
                  id: "start-chat",
                  label: "Start chat",
                  icon: "messageCircle" as const,
                  onSelect: () => {
                    markChatBubbleVisible();
                    window.Pylon?.("showNewMessage");
                  },
                },
              ]
            : []),
          ...(isEnterpriseSupport && !isSelfHosted
            ? [
                {
                  id: "escalate-issue",
                  label: isCreatingIncident ? "Creating incident..." : "Escalate issue",
                  icon: "phoneArrowUpRight" as const,
                  onSelect: () =>
                    escalateIssue({
                      userEmail: userEmail || "Unknown User",
                      projectId,
                      environmentId,
                      currentUrl: `${window.location.origin}${router.asPath}`,
                    }),
                },
              ]
            : []),
        ],
      },
      {
        id: "account",
        items: [
          {
            id: "sign-out",
            label: "Sign out",
            icon: "logOut",
            onSelect: () => {
              void signOutAndResetAnalytics({ callbackUrl: Routes.login });
            },
          },
        ],
      },
    ],
    [
      environmentId,
      escalateIssue,
      isCreatingIncident,
      isEnterpriseSupport,
      isSelfHosted,
      markChatBubbleVisible,
      pylonEnabled,
      projectId,
      router.asPath,
      userEmail,
    ],
  );
};

type ThemeChoice = "light" | "dark" | "system";

const THEME_CHOICES: readonly SliderTabV2Item<ThemeChoice>[] = [
  { id: "light", label: "Light", icon: "sun", iconOnly: true, tooltipContent: "Light" },
  { id: "dark", label: "Dark", icon: "moon", iconOnly: true, tooltipContent: "Dark" },
  { id: "system", label: "System", icon: "computer", iconOnly: true, tooltipContent: "System" },
];

// Laid out on the menu rows' grid (8px inset, a 14px icon slot, an 8px gap) so "Theme" lines up
// with the labels above and below it.
const ThemeMenuRow: React.FC = () => {
  const { resolvedTheme, setTheme, theme } = useTheme();
  const activeTheme: ThemeChoice = theme === "light" || theme === "dark" ? theme : "system";

  return (
    <div className="flex items-center gap-cds-8 py-cds-2 pl-cds-8 pr-cds-2">
      <span className="flex min-w-3.5 justify-center text-cds-ink-subtle dark:text-zinc-400">
        <Icon
          aria-hidden
          name={resolvedTheme === "dark" ? "moon" : "sun"}
          size="sm"
          className="!text-inherit"
        />
      </span>
      <span className="min-w-0 flex-1 text-cds-sm text-cds-ink dark:text-zinc-200">Theme</span>
      <SliderTabsV2<ThemeChoice>
        activeItemId={activeTheme}
        ariaLabel="Theme"
        items={THEME_CHOICES}
        size="sm"
        onChange={(item) => setTheme(item.id)}
      />
    </div>
  );
};

interface ProjectProfileMenuV2Props {
  left: number;
  top: number;
  themeClass?: string;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/** The account menu: who is signed in, the display theme, support, and sign out. */
export const ProjectProfileMenuV2 = React.forwardRef<HTMLDivElement, ProjectProfileMenuV2Props>(
  function ProjectProfileMenuV2Impl(
    { left, top, themeClass, onClose, onMouseEnter, onMouseLeave },
    ref,
  ) {
    const userEmail = useSession().data?.user?.email;
    const sections = useProfileMenuSections();

    return (
      <ViewportAwareFixedMenuV2
        ref={ref}
        className={themeClass}
        left={left}
        top={top}
        width={PROFILE_MENU_WIDTH_PX}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <MenuContainerV2
          className={"w-full max-h-[var(--viewport-aware-fixed-menu-max-height)] overflow-y-auto"}
        >
          <MenuSectionV2>
            <MenuItemV2
              size={"sm"}
              variant={"title"}
              label={userEmail ?? "Signed in"}
              className={"!font-normal"}
            />
          </MenuSectionV2>
          <MenuSectionV2>
            <ThemeMenuRow />
          </MenuSectionV2>
          {sections.map((section) => (
            <MenuSectionV2 key={section.id}>
              {section.items.map((item) => (
                <MenuItemV2
                  key={item.id}
                  size={"sm"}
                  label={item.label}
                  leftIcon={item.icon}
                  rightIcon={item.trailingIcon}
                  onClick={() => {
                    item.onSelect();
                    onClose();
                  }}
                />
              ))}
            </MenuSectionV2>
          ))}
        </MenuContainerV2>
      </ViewportAwareFixedMenuV2>
    );
  },
);

// The menu hangs this far below its button.
const PROFILE_MENU_GUTTER_PX = 8;
// Leaving the button and menu closes the menu after this long, long enough to cross the gutter
// between them; the sidebar's workspace menu and the admin options menu wait as long.
const PROFILE_MENU_CLOSE_DELAY_MS = 100;

/**
 * The signed-in user's avatar at the top rail's right end; opens the account menu below it,
 * right-aligned to the button. It stays put on every page, global settings included.
 */
export const ProjectProfileButtonV2: React.FC<{ themeClass?: string }> = ({ themeClass }) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const user = useSignedInTeamUser();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const menuOpen = menuPosition != null;
  const closeTimerRef = useRef<number | null>(null);
  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current == null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);
  const closeMenu = useCallback(() => {
    clearCloseTimer();
    setMenuPosition(null);
  }, [clearCloseTimer]);
  const scheduleMenuClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setMenuPosition(null);
    }, PROFILE_MENU_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);
  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      // The button toggles the menu itself; closing here as well would reopen it on release.
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, menuOpen]);

  return (
    <>
      <TooltipV2 content={"Account"} placement={"bottom"} persistOnHover={false}>
        <button
          ref={buttonRef}
          type={"button"}
          aria-label={"Account"}
          aria-haspopup={"menu"}
          // Marks the menu as open so the tooltip steps aside for it.
          aria-expanded={menuOpen}
          // The avatar fills it, so hovering darkens the avatar's stroke rather than a fill.
          className={classNames(
            "group block h-7 w-7 shrink-0 rounded-cds-6",
            "focus-visible:outline-none focus-visible:!shadow-cds-focus",
          )}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={() => {
            if (menuOpen) scheduleMenuClose();
          }}
          onClick={() => {
            if (menuOpen) {
              closeMenu();
              return;
            }
            const rect = buttonRef.current?.getBoundingClientRect();
            if (rect == null) return;
            setMenuPosition({
              left: rect.right - PROFILE_MENU_WIDTH_PX,
              top: rect.bottom + PROFILE_MENU_GUTTER_PX,
            });
          }}
        >
          {/* Sized, shaped, and stroked like the search field and button beside it: 28px with 6px
              corners, darkening its stroke on hover as the search field does, and filled as the
              search field is. The `!` beats UserAvatar's own size and `rounded-full`. Without a
              photo, the quiet person icon stands in for a colored initial. */}
          <UserAvatar
            noTooltip
            size={"md"}
            user={user}
            fallbackAppearance={"neutral-user"}
            className={classNames(
              "!h-7 !min-h-7 !w-7 !min-w-7 !rounded-cds-6 border-[1px] transition-colors duration-75",
              "bg-cds-surface-default dark:bg-white/[0.04]",
              menuOpen
                ? "border-black/[0.16] dark:border-white/[0.16]"
                : [
                    "border-cds-surface-border group-hover:border-black/[0.16]",
                    "dark:border-white/10 dark:group-hover:border-white/[0.16]",
                  ],
            )}
          />
        </button>
      </TooltipV2>
      {menuPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ProjectProfileMenuV2
            ref={menuRef}
            left={menuPosition.left}
            top={menuPosition.top}
            themeClass={themeClass}
            onClose={closeMenu}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleMenuClose}
          />,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};
